import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Alert, Button, Spin } from "antd";
import { useAuth } from "../hooks/useAuth";
import {
  fetchAuthorizationRequest,
  submitAuthorizationDecision,
} from "../services/oauthAPI";

const errorMessage = (error, fallback) =>
  error?.response?.data?.detail?.message || fallback;

const OAuthConsent = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id");
  const [authorizationRequest, setAuthorizationRequest] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!requestId) {
      setError("This connection link is incomplete. Start the connection again from the app you are connecting.");
      return;
    }
    fetchAuthorizationRequest(requestId)
      .then(setAuthorizationRequest)
      .catch((err) =>
        setError(errorMessage(err, "This connection request could not be loaded."))
      );
  }, [isAuthenticated, requestId]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const decide = async (approve) => {
    setSubmitting(approve ? "approve" : "deny");
    try {
      const { redirect_url } = await submitAuthorizationDecision(requestId, approve);
      window.location.assign(redirect_url);
    } catch (err) {
      setError(errorMessage(err, "Something went wrong. Start the connection again from the app you are connecting."));
      setSubmitting(null);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-lg shadow-md w-full max-w-md p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Connect to SubmittalLink</h1>

        {error && <Alert type="error" showIcon message={error} />}

        {!error && !authorizationRequest && (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        )}

        {!error && authorizationRequest && (
          <>
            <p className="text-gray-700 mb-4">
              <span className="font-semibold">{authorizationRequest.client_name}</span> wants
              read-only access to your SubmittalLink account.
            </p>

            <div className="bg-gray-50 rounded-md p-4 mb-4 text-sm text-gray-700 space-y-1">
              <p>
                Signed in as{" "}
                <span className="font-medium text-gray-900">
                  {authorizationRequest.user_email}
                </span>
              </p>
              <p>
                You will be sent back to{" "}
                <span className="font-medium text-gray-900">
                  {authorizationRequest.redirect_host}
                </span>
              </p>
            </div>

            <p className="text-sm text-gray-700 mb-2">It will be able to:</p>
            <ul className="list-disc pl-5 text-sm text-gray-700 mb-4 space-y-1">
              <li>See the projects you belong to</li>
              <li>Read the submittals, RFIs and daily reports you can already see in SubmittalLink</li>
            </ul>
            <p className="text-sm text-gray-700 mb-4">
              It cannot create, change or delete anything.
            </p>

            {authorizationRequest.is_local_client && (
              <Alert
                className="mb-4"
                type="warning"
                showIcon
                message="This request sends you back to an app running on this computer. Only allow it if you started the connection yourself."
              />
            )}

            <div className="flex justify-end gap-2">
              <Button
                onClick={() => decide(false)}
                loading={submitting === "deny"}
                disabled={!!submitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => decide(true)}
                loading={submitting === "approve"}
                disabled={!!submitting}
              >
                Allow
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthConsent;
